// src/pages/enterprise-ai/pricingPanel.js
// T6-PRICING-PANEL-01. The Pricing tab's calls and its plain helpers, kept out of the
// page so they can be tested without a browser.
//
// Every call goes to mgmt-backend's /api/united-pricing/* routes (T5-PRICING-ROUTES-01,
// routes/unitedPricingRoutes.js), which forward to org-backend's /api/internal/pricing
// with the internal key. The panel never sends `by`: mgmt-backend writes the signed-in
// staff email itself. All nine routes need united_ai admin.
//
// Units. org-backend stores a multiplier as "x cost" (1.9 = cost plus 90%) and a rate
// as a fraction (0.15 = 15%). Staff type a multiplier as x cost and a rate as a percent;
// toServerValue turns the typed text into what org-backend stores.

import axios from 'axios';
import { T6_BASE, authHeader } from '../enterprise/EnterpriseAgentAccessTab';

// ─────────────────────────────────────────────
// CALLS
// ─────────────────────────────────────────────

export async function pricing(method, path, { body, params } = {}) {
    const headers = await authHeader();
    const res = await axios.request({ method, url: `${T6_BASE}/api/united-pricing${path}`, data: body, params, headers });
    return res.data;
}

const enc = encodeURIComponent;

export const pricingApi = {
    sources: () => pricing('GET', '/sources'),
    overrides: () => pricing('GET', '/overrides'),
    organizations: () => pricing('GET', '/organizations'),
    organization: (orgId) => pricing('GET', '/organizations/' + enc(orgId)),
    setDefault: (source, value, reason) => pricing('PUT', '/sources/' + enc(source) + '/default', { body: { value, reason } }),
    setOverride: (orgId, source, value, reason) =>
        pricing('PUT', '/organizations/' + enc(orgId) + '/overrides/' + enc(source), { body: { value, reason } }),
    clearOverride: (orgId, source, reason) =>
        pricing('DELETE', '/organizations/' + enc(orgId) + '/overrides/' + enc(source), { body: { reason } }),
    preview: (orgId, proposed) => pricing('POST', '/organizations/' + enc(orgId) + '/preview', { body: { proposed } }),
    setRevenueReal: (orgId, on, reason) => pricing('PUT', '/organizations/' + enc(orgId) + '/revenue-real', { body: { on, reason } }),
};

// ─────────────────────────────────────────────
// WHO MAY SEE THE TAB
// ─────────────────────────────────────────────

// The answer to the sources read decides whether this staff member holds the pricing role
// (united_ai admin). The panel cannot read the role itself in production: GET /api/employee/:uid
// sits behind mgmt-backend's legacy gate. So the server's own gate is asked, and the tab stays
// hidden unless the gate let the call through:
//   'allowed'  a 200, or any refusal from org-backend behind the gate (its { ok:false, code })
//   'denied'   401 or 403 from the staff gate, or no answer at all, or a 404 (routes not deployed)
export function accessFromResponse(status, data) {
    if (status >= 200 && status < 300) return 'allowed';
    if (status === 401 || status === 403 || status === 404) return 'denied';
    if (data && typeof data === 'object' && data.ok === false && typeof data.code === 'string') return 'allowed';
    return 'denied';
}

export function accessFromError(err) {
    if (!err || !err.response) return 'denied';
    return accessFromResponse(err.response.status, err.response.data);
}

// ─────────────────────────────────────────────
// VALUES AND WORDS
// ─────────────────────────────────────────────

// org-backend's describeValue (services/pricing/pricingRates.js), word for word.
export function describeValue(kind, v) {
    if (typeof v !== 'number' || !Number.isFinite(v)) return String(v);
    if (kind === 'multiplier') return (Math.round(v * 1000) / 1000) + 'x cost';
    return (Math.round(v * 10000) / 100) + '%';
}

// "1.9x cost (cost plus 90%)" for a multiplier; "15%" for a rate.
export function valueWords(kind, v) {
    if (typeof v !== 'number' || !Number.isFinite(v)) return '—';
    if (kind === 'multiplier') return describeValue(kind, v) + ' (cost plus ' + Math.round((v - 1) * 1000) / 10 + '%)';
    return describeValue(kind, v);
}

export function rangeWords(kind, floor, ceiling) {
    return describeValue(kind, floor) + ' to ' + describeValue(kind, ceiling);
}

// What a stored value looks like in the input box: 1.9 for a multiplier, 15 for a 0.15 rate.
export function toInputValue(kind, v) {
    if (typeof v !== 'number' || !Number.isFinite(v)) return '';
    if (kind === 'multiplier') return String(Math.round(v * 1000) / 1000);
    return String(Math.round(v * 10000) / 100);
}

// Typed text to the value org-backend stores. null when it is not a number.
export function toServerValue(kind, text) {
    const s = String(text == null ? '' : text).trim().replace(/%$/, '').replace(/x$/i, '').trim();
    if (s === '' || !/^-?\d*\.?\d+$/.test(s)) return null;
    const n = Number(s);
    if (!Number.isFinite(n)) return null;
    if (kind === 'multiplier') return n;
    return Math.round(n * 100) / 10000;
}

// org-backend's _boundsSentence, word for word, so the panel refuses an out-of-range value
// before anything is sent with the same sentence the server would answer. The server
// still checks; its own sentence is shown if it ever differs.
export function boundsSentence(label, kind, floor, ceiling, value) {
    return 'The ' + String(label || '').toLowerCase() + ' must be between ' + describeValue(kind, floor) + ' and '
        + describeValue(kind, ceiling) + '. ' + JSON.stringify(value) + ' was not saved.';
}

export function inBounds(v, floor, ceiling) {
    return typeof v === 'number' && Number.isFinite(v) && v >= floor && v <= ceiling;
}

// One typed value checked against its source: { value } or { refusal } (a "CODE: sentence" line).
export function checkTyped(row, text) {
    const v = toServerValue(row.kind, text);
    if (v === null) {
        return { refusal: 'PRICING_OUT_OF_BOUNDS: ' + boundsSentence(row.label, row.kind, row.floor, row.ceiling, String(text).trim()) };
    }
    if (!inBounds(v, row.floor, row.ceiling)) {
        return { refusal: 'PRICING_OUT_OF_BOUNDS: ' + boundsSentence(row.label, row.kind, row.floor, row.ceiling, v) };
    }
    return { value: v };
}

// mgmt-backend's own sentence for a write without a reason (unitedPricingRoutes.js).
export const REASON_REQUIRED_LINE = 'PRICING_REASON_REQUIRED: Say why you are making this change. Nothing was saved.';
export const hasReason = (r) => typeof r === 'string' && r.trim().length > 0;

export function formatUSD(n) {
    if (typeof n !== 'number' || !Number.isFinite(n)) return '—';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

export function formatCents(c) {
    return Number.isFinite(c) ? formatUSD(c / 100) : '—';
}

const daysWords = (d) => (typeof d === 'number' && Number.isFinite(d) ? String(d) : null);

// The two preview lines the approved functions name, from org-backend's preview answer:
//   "At these rates, their last 30 days would have cost $X instead of $Y."
//   "Wallet $B lasts about N days (now M)."
// When nothing was charged in the window the days are null and org-backend's note is shown.
export function previewLines(p) {
    if (!p || p.ok === false) return null;
    const window = p.windowDays || 30;
    const cur = p.current || {};
    const prop = p.proposed || {};
    const balance = formatCents(p.balance && p.balance.totalCents);
    const n = daysWords(prop.days);
    const m = daysWords(cur.days);
    return {
        costLine: 'At these rates, their last ' + window + ' days would have cost ' + formatUSD(prop.totalUSD)
            + ' instead of ' + formatUSD(cur.totalUSD) + '.',
        walletLine: n !== null
            ? 'Wallet ' + balance + ' lasts about ' + n + ' days (now ' + (m !== null ? m : 'no spending') + ').'
            : 'Wallet ' + balance + ': no spending in the last ' + window + ' days to divide it by.',
        days: n,
        nowDays: m,
        notes: Array.isArray(p.notes) ? p.notes : [],
    };
}

// The one line under the switch (PM1's rule, REVENUE-REAL-SWITCH-01).
export const REVENUE_SWITCH_LINE = 'Turning this on counts charges from that moment as real revenue. It does not end the customer\'s test mode for mail or money: fake drivers, no real mail and no real money stay exactly as they are.';

export function revenueStateWords(revenue) {
    if (!revenue) return 'Switch unread';
    if (revenue.countsAsReal) {
        return revenue.realSince ? 'Real since ' + new Date(revenue.realSince).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Counted as real';
    }
    return revenue.isTestOrg ? 'Test: shown as a grey line in the money email' : 'Not counted as real';
}

// Every change in every source's history, newest first, one row each.
export function historyRows(sources) {
    const rows = [];
    for (const s of sources || []) {
        for (const h of s.history || []) {
            rows.push({ ...h, source: s.source, label: s.label, kind: s.kind });
        }
    }
    rows.sort((a, b) => new Date(b.at).getTime() - new Date(a.at).getTime());
    return rows;
}
